import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAgency, applyBrandColor } from "../../contexts/AgencyContext";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, Save, Upload, CheckCircle2 } from "lucide-react";

const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "Pacific/Honolulu", "America/Phoenix",
];
const PAYROLL = ["weekly", "biweekly", "semimonthly", "monthly"];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function AgencyProfile() {
  const { agency, updateAgency } = useAgency();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (agency) setForm(agency);
  }, [agency]);

  if (!form) return <div className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</div>;

  const editable = ["platform_owner", "agency_admin"].includes(user?.role);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setHours = (day, val) => setForm((f) => ({ ...f, office_hours: { ...f.office_hours, [day]: val } }));
  const setNotif = (k, v) => setForm((f) => ({ ...f, notification_settings: { ...f.notification_settings, [k]: v } }));

  const onColorChange = (v) => {
    set("brand_primary_color", v);
    applyBrandColor(v);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null); setSaved(false);
    try {
      const payload = { ...form };
      // strip server-only fields
      ["id", "created_at", "updated_at", "is_active", "logo_url"].forEach((k) => delete payload[k]);
      const { data } = await api.put(`/agencies/${form.id}`, payload);
      updateAgency(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/agencies/${form.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...form, logo_url: data.logo_url };
      setForm(updated);
      updateAgency(updated);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="agency-profile-form">
      {/* Identity */}
      <section className="surface p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading-section">Identity</h3>
          {saved && <span className="pill-success" data-testid="profile-saved-badge"><CheckCircle2 className="w-3 h-3" /> Saved</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Agency Name" value={form.name} onChange={(v) => set("name", v)} required disabled={!editable} testid="profile-name" />
          <Field label="Primary Contact" value={form.primary_contact_name} onChange={(v) => set("primary_contact_name", v)} disabled={!editable} />
          <Field label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} disabled={!editable} testid="profile-email" />
          <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} disabled={!editable} />
          <Field label="Website" value={form.website} onChange={(v) => set("website", v)} disabled={!editable} placeholder="https://…" />
          <Field label="Primary Contact Email" type="email" value={form.primary_contact_email} onChange={(v) => set("primary_contact_email", v)} disabled={!editable} />
          <div className="md:col-span-2">
            <Field label="Address" value={form.address} onChange={(v) => set("address", v)} disabled={!editable} />
          </div>
        </div>
      </section>

      {/* Logo */}
      <section className="surface p-5 md:p-6">
        <h3 className="heading-section mb-4">Logo</h3>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl border border-border bg-secondary flex items-center justify-center overflow-hidden" data-testid="logo-preview">
            {form.logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <span className="text-xl font-bold" style={{ color: "hsl(var(--primary))" }}>{form.name?.charAt(0)}</span>
            )}
          </div>
          {editable && (
            <label className="btn-outline cursor-pointer">
              <Upload className="w-4 h-4" /> Upload logo
              <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="logo-upload" />
            </label>
          )}
        </div>
      </section>

      {/* Brand */}
      <section className="surface p-5 md:p-6">
        <h3 className="heading-section mb-4">Brand Colors</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ColorField label="Primary" value={form.brand_primary_color} onChange={onColorChange} disabled={!editable} testid="primary-color" />
          <ColorField label="Secondary / Accent" value={form.brand_secondary_color} onChange={(v) => set("brand_secondary_color", v)} disabled={!editable} />
        </div>
        <div className="mt-4 text-xs text-muted-foreground">
          The primary color is applied to navigation, buttons, and links throughout your workspace. Changes preview live.
        </div>
      </section>

      {/* Locale / Compliance */}
      <section className="surface p-5 md:p-6">
        <h3 className="heading-section mb-4">Operations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select label="Timezone" value={form.timezone} onChange={(v) => set("timezone", v)} options={TIMEZONES} disabled={!editable} />
          <Select label="Payroll Cycle" value={form.payroll_cycle} onChange={(v) => set("payroll_cycle", v)} options={PAYROLL} disabled={!editable} />
          <div className="md:col-span-2">
            <Field label="Service Area" value={form.service_area} onChange={(v) => set("service_area", v)} disabled={!editable} placeholder="Counties / cities served" />
          </div>
          <div className="md:col-span-2">
            <Field label="Licensing Information" value={form.licensing_info} onChange={(v) => set("licensing_info", v)} disabled={!editable} placeholder="License #, issuing state, expiry" />
          </div>
        </div>
      </section>

      {/* Office Hours */}
      <section className="surface p-5 md:p-6">
        <h3 className="heading-section mb-4">Office Hours</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {DAYS.map((d) => (
            <Field key={d} label={d.charAt(0).toUpperCase() + d.slice(1)} value={form.office_hours?.[d] || ""} onChange={(v) => setHours(d, v)} disabled={!editable} placeholder="9:00–17:00 (or 'Closed')" />
          ))}
        </div>
      </section>

      {/* Notifications */}
      <section className="surface p-5 md:p-6">
        <h3 className="heading-section mb-4">Default Notification Settings</h3>
        <div className="space-y-3">
          <Toggle label="Application submitted" value={form.notification_settings?.application_submitted} onChange={(v) => setNotif("application_submitted", v)} disabled={!editable} />
          <Toggle label="Documents uploaded" value={form.notification_settings?.documents_uploaded} onChange={(v) => setNotif("documents_uploaded", v)} disabled={!editable} />
          <Toggle label="Interview requested" value={form.notification_settings?.interview_requested} onChange={(v) => setNotif("interview_requested", v)} disabled={!editable} />
          <Toggle label="Status changed" value={form.notification_settings?.status_changed} onChange={(v) => setNotif("status_changed", v)} disabled={!editable} />
        </div>
      </section>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      {editable && (
        <div className="flex justify-end sticky bottom-4">
          <button type="submit" disabled={saving} className="btn-primary shadow-elevated" data-testid="save-agency-profile">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save changes</>}
          </button>
        </div>
      )}
    </form>
  );
}

function Field({ label, value, onChange, disabled, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1.5">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        className="input-base disabled:opacity-60"
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, disabled }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input-base disabled:opacity-60" disabled={disabled}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function ColorField({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#06372B"} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="w-10 h-10 rounded-lg cursor-pointer border border-border" data-testid={testid} />
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="input-base font-mono uppercase" />
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange, disabled }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
      <span className="text-sm">{label}</span>
      <button
        type="button"
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled}
        className={`relative w-10 h-5 rounded-full transition-colors ${value ? "" : "bg-secondary"}`}
        style={value ? { backgroundColor: "hsl(var(--primary))" } : {}}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}
